'use client';

import { Button, cn, Skeleton } from '@order-up/common';
import type { Tenant } from '@order-up/models';
import { Building } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { createLogger } from '../utils/logger';
import { getAdminServiceUrl } from '../utils/env';
import { useAuth } from '../hooks/useAuth';
import { useCurrentUserAdmin } from '../hooks/useCurrentUserAdmin';
import { useGetTenantsByUserId } from '../hooks/useTenants';
import { useUserSessionValidation } from '../hooks/useUserAuth';

const logger = createLogger('TenantSelector');

interface TenantSelectorProps {
  onSelect: (tenant: Tenant) => void;
  title?: string;
  description?: string;
  emptyMessage?: string;
}

export function TenantSelector({
  onSelect,
  title: _title = "Select a Tenant",
  description: _description = "Choose which tenant you want to sign in to.",
  emptyMessage = "You are not associated with any tenants."
}: TenantSelectorProps) {
  const { data: authData } = useAuth();
  const { authUser } = authData || {};
  const { data: sessionUser } = useUserSessionValidation();
  const hasAutoSelectedRef = useRef(false);

  // Use user ID from either Firebase auth or session
  const userId = authUser?.uid || sessionUser?.uid || '';
  const { data: tenants, isLoading, error } = useGetTenantsByUserId(userId);
  // A platform admin has no tenant assignments BY DESIGN — they live in
  // `admins/{uid}`, not `users/{uid}`, and reach a restaurant through a support
  // session. Read without a tenant, which is the whole point: `useUserRole`
  // resolves nothing until one is selected, so it cannot answer this here.
  const { isAdmin, isLoading: isAdminLoading } = useCurrentUserAdmin();
  const hasNoTenants = !tenants || tenants.length === 0;

  logger.debug('User ID sources', {
    authUserId: authUser?.uid,
    sessionUserId: sessionUser?.uid,
    finalUserId: userId,
    tenantsCount: tenants?.length || 0
  });

  // Auto-select if only one tenant available
  useEffect(() => {
    if (tenants && tenants.length === 1 && !isLoading && !error && !hasAutoSelectedRef.current) {
      logger.debug('Auto-selecting single tenant', { tenantName: tenants[0].displayName });
      hasAutoSelectedRef.current = true;
      onSelect(tenants[0]);
    }
  }, [tenants?.length, isLoading, error]);

  // Also held while the admin check is pending on an EMPTY list, so an admin
  // does not see "not associated with any tenants" flash before the right
  // message replaces it. A populated list never waits on it.
  if (isLoading || (hasNoTenants && !error && isAdminLoading)) {
    return (
      <div className="space-y-2">
        {/* Show 3 skeleton organization options while loading */}
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 bg-linear-to-r from-card/50 to-card/30 backdrop-blur-sm border border-border/30 rounded-lg">
            <Skeleton className="size-8 sm:size-9 rounded-lg" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-3/4 rounded" />
              <Skeleton className="h-3 w-1/2 rounded" />
            </div>
            <Skeleton className="h-3 w-12 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="bg-linear-to-br from-destructive/10 to-destructive/5 text-destructive rounded-2xl p-4 mb-6 ring-1 ring-destructive/10 shadow-lg shadow-destructive/5">
          <Building className="h-8 w-8" />
        </div>
        <div className="text-center space-y-2">
          <p className="font-semibold text-destructive text-lg">Error loading organizations</p>
          <p className="text-sm text-muted-foreground bg-muted/30 px-3 py-1.5 rounded-lg border border-border/50">{error.message}</p>
        </div>
      </div>
    );
  }

  // 🔴 AN ADMIN WITH NO TENANTS IS NOT AN ERROR AND NOT "NOT ASSOCIATED".
  //
  // This screen used to tell a site admin "Error loading organizations —
  // USER_NOT_FOUND: User profile not found": the assignments route 404'd on the
  // `users/{uid}` document admins correctly do not have, and a correct account
  // read as a broken one (reported as a broken login, 2026-09-15). The route now
  // answers "no assignments", and this says what that means and where to go.
  if (hasNoTenants && isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="bg-linear-to-br from-muted/30 to-muted/10 text-muted-foreground rounded-2xl p-5 mb-6 ring-1 ring-border/20 shadow-sm">
          <Building className="h-10 w-10" />
        </div>
        <div className="text-center space-y-3 max-w-sm">
          <p className="font-semibold text-foreground text-lg">
            You&apos;re signed in as an OrderUp administrator
          </p>
          <p className="text-sm text-muted-foreground">
            Administrator accounts aren&apos;t members of a restaurant, so there is
            nothing to choose here. To work inside a restaurant, open it from the
            admin app with <span className="font-medium text-foreground">Enter as support</span>.
          </p>
          <Button asChild variant="outline" size="sm">
            <a href={getAdminServiceUrl()}>Open the admin app</a>
          </Button>
        </div>
      </div>
    );
  }

  if (hasNoTenants) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="bg-linear-to-br from-muted/30 to-muted/10 text-muted-foreground rounded-2xl p-5 mb-6 ring-1 ring-border/20 shadow-sm">
          <Building className="h-10 w-10" />
        </div>
        <p className="text-muted-foreground text-center font-medium text-lg bg-muted/20 px-4 py-2 rounded-lg border border-border/30">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {tenants.map((tenant: Tenant, index: number) => (
        <Button
          key={tenant.tenantId}
          variant="ghost"
          className={cn(
            "w-full justify-start h-auto group",
            "bg-linear-to-r from-card/50 to-card/30 backdrop-blur-sm",
            "border border-border/30 rounded-lg",
            "hover:bg-linear-to-r hover:from-primary/5 hover:to-primary/10",
            "hover:border-primary/30 hover:shadow-md hover:shadow-primary/5",
            "hover:scale-[1.01] active:scale-[0.99]",
            "transition-all duration-300 ease-out",
            "animate-in fade-in-50 slide-in-from-left-8",
            "p-2.5 sm:p-3"
          )}
          style={{ animationDelay: `${index * 100}ms` }}
          onClick={() => onSelect(tenant)}
        >
          <div className="flex items-center gap-2.5 sm:gap-3 w-full">
            <div className={cn(
              "bg-linear-to-br from-primary/10 via-primary/5 to-transparent",
              "text-primary flex items-center justify-center rounded-lg",
              "ring-1 ring-primary/10 shadow-sm shadow-primary/5",
              "group-hover:ring-primary/25 group-hover:shadow-primary/10",
              "group-hover:scale-105 group-hover:rotate-2",
              "transition-all duration-300 ease-out",
              "size-8 sm:size-9"
            )}>
              <Building className="size-3.5 sm:size-4" />
            </div>
            <div className='text-left flex-1 min-w-0'>
              <p className={cn(
                "font-semibold text-foreground leading-tight",
                "group-hover:text-primary transition-colors duration-300",
                "text-sm truncate"
              )}>
                {tenant.displayName}
              </p>
              <p className="text-xs text-muted-foreground/80 font-normal mt-0.5 truncate">
                {tenant.name}
              </p>
            </div>
            <div className={cn(
              "opacity-0 group-hover:opacity-100 transition-all duration-300",
              "group-hover:translate-x-0.5 transform"
            )}>
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-primary motion-safe:animate-pulse"></div>
                <div className="text-primary font-medium text-xs">Select</div>
              </div>
            </div>
          </div>
        </Button>
      ))}
    </div>
  );
}
